import { ActionTree } from 'vuex';
import {
  mutationSetError,
  mutationSetToken,
  mutationSetAuthenticationSteps,
  mutationSetAuthenticationAnswers,
  mutationSetStatus,
  mutationVerifySms,
  mutationSetSubmittingResponse,
  mutationSetSendPinRetryCount,
  mutationSetRecipientFirstOpen,
} from '@signing-page/session/store/session.mutations';
import { IRootState } from '@signing-page/store/store.ts';
import { ISessionState } from '@signing-page/session/store/session.store';
import { SessionService } from '@signing-page/api/session/services/session.service';
import { IAuthenticationStepInput } from '@signing-page/authentication/types/authentication-step-input';
import { IAuthenticationStep } from '@signing-page/authentication/types/authentication-step';
import { EAuthenticationStepType } from '@signing-page/authentication/types/enums/authentication-step-type';
import { translate } from '@/helpers/translation.helper';
import { IToken } from '@signing-page/session/type/token';
import { EDocumentStatus } from '@signing-page/documents/types/enums/document-status';
import { AuthenticationHelper } from '@signing-page/authentication/helpers/authentication.helper';
import { HttpThrottleError, HttpLockedError } from '@signing-page/common/errors';
import { alertNamespace, EAlertAction } from '@signing-page/alert/store/alert.constants';

export const actionLoadToken = 'loadToken';
export const actionLoadTokenSuccess = 'loadTokenSuccess';
export const actionLoadTokenFailed = 'loadTokenFailed';

export const actionSetAuthenticationSteps = 'setAuthenticationSteps';
export const actionSetAuthenticationAnswers = 'setAuthenticationAnswers';

export const actionSubmitAnswer = 'submitAnswer';
export const actionSubmitAnswerFailed = 'submitAnswerFailed';

export const actionSubmitQnaAnswer = 'submitQnaAnswer';
export const actionSubmitQnaAnswerFailed = 'submitQnaAnswerFailed';

export const actionSubmitSmsAnswer = 'submitSmsAnswer';
export const actionSubmitSmsAnswerFailed = 'submitSmsAnswerFailed';

export const actionSendSmsCode = 'sendSmsCode';
export const actionSendSmsCodeFailed = 'sendSmsCodeFailed';
export const actionSendSmsCodeThrottleFailed = 'sendSmsCodeThrottleFailed';
export const actionSendSmsCodeLockedFailed = 'sendSmsCodeLockedFailed';

export const actionSubmitConsent = 'submitConsent';
export const actionSubmitConsentFailed = 'submitConsentFailed';

export const actionDeclineConsent = 'declineConsent';

export const actionHandleGdprConsent = 'handleGdprConsent';
export const actionHandleTrackingConsent = 'handleTrackingConsent';

export const actionSetStatus = 'setStatus';
export const actionSetPreviewToken = 'setPreviewToken';
export const actionSetRecipientFirstOpen = 'setRecipientFirstOpen';

export const actions: ActionTree<ISessionState, IRootState> = {
  async [actionLoadToken](
    { dispatch, rootState },
    { authStepsInput }: { authStepsInput?: IAuthenticationStepInput[] }
  ) {
    try {
      const token: IToken = await SessionService.getToken(
        rootState.documentId,
        rootState.recipientId,
        rootState.authId,
        authStepsInput
      );
      dispatch(actionLoadTokenSuccess, { token, authStepsInput });
    } catch (e) {
      dispatch(actionLoadTokenFailed, e);
    }
  },
  [actionLoadTokenSuccess](
    { commit, dispatch },
    {
      token,
      authStepsInput,
    }: {
      token: IToken;
      authStepsInput: IAuthenticationStepInput[];
    }
  ) {
    commit(mutationSetSubmittingResponse, false);
    if (!!token.authSteps && !!token.authSteps.length) {
      dispatch(actionSetAuthenticationSteps, token.authSteps);

      if (authStepsInput) {
        dispatch(actionSetAuthenticationAnswers, {
          authSteps: token.authSteps,
          authAnswers: authStepsInput,
        });
      }
    } else if (token.metaData && token.metaData.redirectUrl) {
      const pathRegExp = /\/([a-z,\d]+)\/([a-z,\d]+)\/a\/([a-z,\d]+)/g;

      const redirectUrl: string = window.location.href.replace(
        pathRegExp,
        token.metaData.redirectUrl
      );

      window.location.assign(`${redirectUrl}?token=${token.jwt}`);
    } else if (token.jwt) {
      commit(mutationSetToken, token);
      commit(mutationSetStatus, token.status);
      commit(mutationSetAuthenticationSteps, []);
    } else if (token.status) {
      commit(mutationSetStatus, token.status);
    } else {
      commit(mutationSetError, true);
    }
  },
  [actionLoadTokenFailed]({ commit }) {
    commit(mutationSetSubmittingResponse, false);
    commit(mutationSetError, true);
  },
  [actionSetAuthenticationSteps]({ commit }, steps: IAuthenticationStep[]) {
    commit(mutationSetAuthenticationSteps, steps);
  },
  [actionSetAuthenticationAnswers](
    { commit },
    {
      authSteps,
      authAnswers,
    }: { authSteps: IAuthenticationStep[]; authAnswers: IAuthenticationStepInput[] }
  ) {
    const stepsWithError: IAuthenticationStep[] = authSteps.filter(
      (step: IAuthenticationStep) => step.data.error
    );
    const correctAnswers: IAuthenticationStepInput[] = authAnswers.filter(
      (answer: IAuthenticationStepInput) => {
        return !stepsWithError.some((step: IAuthenticationStep) => step.type === answer.type);
      }
    );
    commit(mutationSetAuthenticationAnswers, correctAnswers);
  },
  async [actionSubmitAnswer](
    { commit, dispatch, state },
    authStepsInput: IAuthenticationStepInput[]
  ) {
    try {
      const existingAuthStepsInput: IAuthenticationStepInput[] = [...state.authentication.answers];
      authStepsInput.forEach((authStepInput: IAuthenticationStepInput) => {
        const alreadyHasAnswer: boolean = state.authentication.answers.some(
          (answer: IAuthenticationStepInput) => answer.type === authStepInput.type
        );

        if (alreadyHasAnswer) {
          const index: number = state.authentication.answers.findIndex(
            (answer: IAuthenticationStepInput) => answer.type === authStepInput.type
          );
          existingAuthStepsInput.splice(index, 1, authStepInput);
        } else {
          existingAuthStepsInput.push(authStepInput);
        }
      });
      dispatch(actionLoadToken, { authStepsInput: existingAuthStepsInput });
    } catch (e) {
      commit(mutationSetSubmittingResponse, false);
      console.error(e);
    }
  },
  async [actionSendSmsCode]({ commit, dispatch, state, rootState }) {
    try {
      await SessionService.postSendSmsLegacy(
        rootState.documentId,
        rootState.recipientId,
        rootState.authId,
        state.sendPinRetryCount
      );
      commit(mutationSetSendPinRetryCount, state.sendPinRetryCount + 1);
    } catch (e) {
      if (e instanceof HttpThrottleError) {
        dispatch(actionSendSmsCodeThrottleFailed);
      } else if (e instanceof HttpLockedError) {
        dispatch(actionSendSmsCodeLockedFailed);
      } else {
        dispatch(actionSendSmsCodeFailed);
      }
    }
  },
  [actionSendSmsCodeFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate('could-not-send-sms-code'), {
      root: true,
    });
  },
  [actionSendSmsCodeThrottleFailed]({ dispatch }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-send-sms-code-throttle'),
      {
        root: true,
      }
    );
  },
  [actionSendSmsCodeLockedFailed]({ dispatch }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-send-sms-code-locked'),
      {
        root: true,
      }
    );
  },
  async [actionSubmitSmsAnswer](
    { commit, dispatch, rootState },
    { authStepsInput }: { authStepsInput: IAuthenticationStepInput }
  ) {
    commit(mutationSetSubmittingResponse, true);
    try {
      const verified: boolean = await SessionService.postVerifySmsLegacy(
        authStepsInput,
        rootState.documentId,
        rootState.recipientId,
        rootState.authId
      );
      if (verified) {
        dispatch(actionSubmitAnswer, [authStepsInput]);
      } else {
        dispatch(actionSubmitSmsAnswerFailed);
      }
    } catch (e) {
      dispatch(actionSubmitSmsAnswerFailed);
    }
  },
  [actionSubmitSmsAnswerFailed]({ commit }) {
    commit(mutationSetSubmittingResponse, false);
    commit(mutationVerifySms, false);
  },
  async [actionSubmitQnaAnswer](
    { commit, dispatch, rootState },
    { authStep }: { authStep: IAuthenticationStepInput }
  ) {
    commit(mutationSetSubmittingResponse, true);
    try {
      await SessionService.postQAAnswerLegacy(
        authStep,
        rootState.documentId,
        rootState.recipientId,
        rootState.authId
      );
      dispatch(actionSubmitAnswer, [authStep]);
    } catch (e) {
      dispatch(actionSubmitQnaAnswerFailed);
    }
  },
  [actionSubmitQnaAnswerFailed]({ commit, dispatch }) {
    commit(mutationSetSubmittingResponse, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-save-qna-answer'),
      {
        root: true,
      }
    );
  },
  async [actionSubmitConsent]({ dispatch }, authStepsInput: IAuthenticationStepInput[]) {
    for (let i = 0; i < authStepsInput.length; i++) {
      switch (authStepsInput[i].type) {
        case EAuthenticationStepType.GdprConsent:
          await dispatch(actionHandleGdprConsent, authStepsInput[i]);
          break;
        case EAuthenticationStepType.TrackingConsent:
          await dispatch(actionHandleTrackingConsent, authStepsInput[i]);
          break;
      }
    }
    dispatch(actionSubmitAnswer, authStepsInput);
  },
  [actionDeclineConsent]({ dispatch, state }) {
    const declinedGDPRConsent = AuthenticationHelper.declinedConsent(
      state.authentication.steps,
      EAuthenticationStepType.GdprConsent
    );
    const declinedTrackingConsent = AuthenticationHelper.declinedConsent(
      state.authentication.steps,
      EAuthenticationStepType.TrackingConsent
    );
    const authStepsInput: IAuthenticationStepInput[] = [];
    if (declinedGDPRConsent) {
      authStepsInput.push({ value: '0', type: EAuthenticationStepType.GdprConsent });
    }
    if (declinedTrackingConsent) {
      authStepsInput.push({ value: '0', type: EAuthenticationStepType.TrackingConsent });
    }
    dispatch(actionSubmitConsent, authStepsInput);
  },
  async [actionHandleGdprConsent](
    { commit, dispatch, rootState },
    authStepInput: IAuthenticationStepInput
  ) {
    commit(mutationSetSubmittingResponse, true);
    try {
      if (authStepInput.value === '1') {
        await SessionService.postVerifyConsentLegacy(
          rootState.documentId,
          rootState.recipientId,
          rootState.authId
        );
      } else {
        await SessionService.postRejectConsentLegacy(
          rootState.documentId,
          rootState.recipientId,
          rootState.authId
        );
      }
      commit(mutationSetSubmittingResponse, false);
    } catch (error) {
      console.log(error);
      dispatch(actionSubmitConsentFailed, error);
    }
  },
  async [actionHandleTrackingConsent](
    { dispatch, commit, rootState },
    authStepInput: IAuthenticationStepInput
  ) {
    commit(mutationSetSubmittingResponse, true);
    try {
      if (authStepInput.value === '1') {
        await SessionService.postVerifyTrackingConsentLegacy(
          rootState.documentId,
          rootState.recipientId,
          rootState.authId
        );
      } else {
        await SessionService.postRejectTrackingConsentLegacy(
          rootState.documentId,
          rootState.recipientId,
          rootState.authId
        );
      }
      commit(mutationSetSubmittingResponse, true);
    } catch (error) {
      dispatch(actionSubmitConsentFailed);
    }
  },
  [actionSubmitConsentFailed]({ commit, dispatch }) {
    commit(mutationSetSubmittingResponse, false);
    dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate('could-not-save-consent'), {
      root: true,
    });
  },
  [actionSetStatus]({ commit }, status: EDocumentStatus) {
    commit(mutationSetStatus, status);
  },
  [actionSetPreviewToken]({ commit }, token: string) {
    commit(mutationSetToken, { jwt: token });
  },
  [actionSetRecipientFirstOpen]({ commit }, value: boolean) {
    commit(mutationSetRecipientFirstOpen, value);
  },
};
